import React from "react";
import { ProgressList } from "../../../../type/common";
import { cn } from "../../../../lib/utils";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCcw,
  XIcon,
  Play,
  Info,
  Circle,
  Coins,
} from "lucide-react";
import { Button } from "../../../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import { Badge } from "../../../../components/ui/badge";

interface ProgressProps {
  progress: ProgressList[];
  className?: string;
}

const Progress: React.FC<ProgressProps> = ({ progress, className }) => {
  // Render status icon based on the status
  const renderStatusIcon = (status: ProgressList["status"]) => {
    switch (status) {
      case "success":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "error":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "running":
        return (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/50" />
        );
      case "need_validation":
        return <Info className="h-5 w-5 text-muted-foreground" />;
      case "pending":
      default:
        return <Circle className="h-5 w-5 text-muted-foreground/50" />;
    }
  };

  // Render action buttons based on the status and available actions
  const renderActionButtons = (item: ProgressList) => {
    const { status, retryAction, cancelAction, processAction } = item;

    return (
      <TooltipProvider>
        <div className="flex gap-1">
          {status === "error" && retryAction && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={retryAction}>
                  <RefreshCcw />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>Retry</p>
              </TooltipContent>
            </Tooltip>
          )}

          {status === "need_validation" && (
            <>
              {cancelAction && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={cancelAction}
                    >
                      <XIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Cancel</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {processAction && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={processAction}
                    >
                      <Play />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Process</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </TooltipProvider>
    );
  };

  return (
    <div className={cn("flex flex-col", className)}>
      {progress.map((item, index) => {
        // Calculate if the item is active or completed
        const isCompleted = item.status === "success";
        const isActive =
          item.status === "running" ||
          item.status === "need_validation" ||
          item.status === "error";
        const isPending = item.status === "pending";
        const isLast = index === progress.length - 1;

        return (
          <div key={`${item.step}-${index}`} className="relative">
            {/* Step item */}
            <div
              className={cn(
                "flex items-stretch py-2 px-3 transition-colors",
                isPending && "text-muted-foreground"
              )}
            >
              <div className="relative flex flex-col items-center mr-3">
                <div className="flex-shrink-0">
                  {renderStatusIcon(item.status)}
                </div>

                {!isLast && (
                  <div className="absolute top-6.5 h-[calc(100%-16px)] w-px bg-border z-0" />
                )}
              </div>

              <div className="flex flex-col flex-grow min-w-0">
                <div className="flex justify-between items-start w-full">
                  <div className="flex flex-col overflow-hidden">
                    <div className="flex items-center gap-2 pb-1">
                      <h4 className={cn("font-medium text-sm")}>
                        {item.label}
                      </h4>
                      {item.tokenCost !== undefined && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="gap-1 h-5 px-1.5 bg-muted/50"
                              >
                                <Coins className="h-3 w-3" />
                                <span className="text-xs">
                                  {item.tokenCost.toLocaleString()}
                                </span>
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              className="max-w-[240px]"
                            >
                              <p className="text-xs text-center">
                                {item.tokenCostDescription ||
                                  "Token cost for this operation"}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <p className={cn("text-xs text-muted-foreground")}>
                      {item.description}
                    </p>
                  </div>

                  <div className="flex-shrink-0 ml-2">
                    {renderActionButtons(item)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Progress;
